import { ArrowRight, CheckCircle, Package, Plus, Truck, WarningCircle, Wrench } from 'shared/icons';
import Badge from '../../ui/Badge';
import { Mono } from '../../ui/Mono';
import {
    BalanceLineKey,
    BalanceLineTitle,
    FormatQty,
    HOLDER_LPN,
    HOLDER_PRODUCTION_ORDER,
    HOLDER_SHIPMENT_VOLUME,
    HolderDirection,
} from './repackModel';

/**
 * SALDO ANTES × DEPOIS — a tela que o contrato do M7 exige antes de confirmar.
 *
 * ── A regra que este componente serve ───────────────────────────────────────
 * "Ver o saldo antes × depois ANTES de confirmar (mesma disciplina da prévia de
 * alocação do M5: o que a tela promete é o que o banco vai gravar)." Por isso
 * **nenhum número daqui é calculado no navegador**: `qty_before`, `qty_after`,
 * `emptied` e `balanced` chegam prontos do `dry_run`, que roda a MESMA conta da
 * gravação. Uma tela que somasse por conta própria teria que acertar por
 * coincidência.
 *
 * ── Como a leitura foi organizada (a decisão de interação) ──────────────────
 * 1. **O veredito primeiro.** Uma faixa de uma linha responde a pergunta que o
 *    Supervisor faz de verdade: "não vai sumir nada?". Verde = a soma por
 *    produto é idêntica antes e depois.
 * 2. **Um cartão por LUGAR, na ordem do movimento**: de onde SAI, depois para
 *    onde VAI, por último o que não mudou. O leitor segue a mercadoria na
 *    mesma ordem em que ela anda no chão.
 * 3. **Dois números grandes por cartão** (`antes → depois`) e o delta como
 *    etiqueta colorida. O detalhe por produto/lote fica embaixo, com as linhas
 *    QUE MUDARAM em destaque e as intocadas apagadas: um pallet com oito itens
 *    dos quais dois se movem não pode obrigar ninguém a caçar quais são.
 * 4. **O que vai FECHAR aparece como consequência declarada**, com o motivo
 *    escrito (§6.2: o pallet fecha porque ZEROU, nunca por ter sido
 *    desmembrado). É a diferença entre um aviso e uma acusação.
 */

const HOLDER_ICONS = {
    [HOLDER_LPN]: Package,
    [HOLDER_PRODUCTION_ORDER]: Wrench,
    [HOLDER_SHIPMENT_VOLUME]: Truck,
};

/** Prioridade de leitura: primeiro quem entrega, depois quem recebe. */
const DIRECTION_ORDER = { OUT: 0, IN: 1, SAME: 2 };

function DeltaBadge({ before, after }) {
    const delta = Number(after ?? 0) - Number(before ?? 0);
    if (delta === 0) {
        return <Badge variant="neutral">±0</Badge>;
    }
    return (
        <Badge variant={delta > 0 ? 'success' : 'warning'}>
            {`${delta > 0 ? '+' : '−'}${FormatQty(Math.abs(delta))}`}
        </Badge>
    );
}

const BeforeAfter = ({ label, before, after, size = 'lg' }) => {
    const numberClass = size === 'lg' ? 'text-2xl font-black' : 'text-base font-bold';
    return (
        <div className="flex items-center justify-center gap-3">
            <div className="text-center">
                <p className="text-[11px] uppercase tracking-wide text-mono-500 dark:text-mono-400">
                    {label('wms.repack.balance_before')}
                </p>
                <Mono className={`block ${numberClass} text-mono-500 dark:text-mono-400`}>{FormatQty(before)}</Mono>
            </div>
            <ArrowRight size={size === 'lg' ? 22 : 16} className="shrink-0 text-mono-400" />
            <div className="text-center">
                <p className="text-[11px] uppercase tracking-wide text-mono-500 dark:text-mono-400">
                    {label('wms.repack.balance_after')}
                </p>
                <Mono className={`block ${numberClass} text-black dark:text-white`}>{FormatQty(after)}</Mono>
            </div>
        </div>
    );
};

const HolderCard = ({ holder, label }) => {
    const Icon = HOLDER_ICONS[holder.kind] || Package;
    const direction = HolderDirection(holder);
    const toneClass =
        direction === 'IN'
            ? 'border-success-500 dark:border-success-600'
            : direction === 'OUT'
              ? 'border-mono-400 dark:border-mono-600'
              : 'border-mono-200 dark:border-mono-800';

    return (
        <div className={`space-y-3 rounded-xl border-2 bg-white p-4 dark:bg-mono-950 ${toneClass}`}>
            <div className="flex items-start gap-2">
                <Icon size={20} className="mt-0.5 shrink-0 text-mono-500" />
                <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        {label(`wms.repack.holder.${holder.kind}`)}
                    </p>
                    <Mono className="block break-all text-base font-bold text-black dark:text-white">
                        {holder.key}
                    </Mono>
                    {holder.label && holder.label !== holder.key ? (
                        <p className="mt-0.5 text-xs text-mono-500 dark:text-mono-400">{holder.label}</p>
                    ) : null}
                </div>
                <DeltaBadge before={holder.qty_before} after={holder.qty_after} />
            </div>

            <BeforeAfter label={label} before={holder.qty_before} after={holder.qty_after} />

            {holder.balances.length > 0 ? (
                <ul className="space-y-1 border-t border-mono-100 pt-2 dark:border-mono-900">
                    {holder.balances.map((row) => {
                        const changed = Number(row.qty_after ?? 0) !== Number(row.qty_before ?? 0);
                        return (
                            <li
                                key={BalanceLineKey(row)}
                                className={`flex items-center justify-between gap-3 text-sm ${
                                    changed ? '' : 'opacity-45'
                                }`}
                            >
                                <Mono
                                    className={`min-w-0 flex-1 truncate ${
                                        changed
                                            ? 'font-semibold text-black dark:text-white'
                                            : 'text-mono-600 dark:text-mono-400'
                                    }`}
                                    title={BalanceLineTitle(row)}
                                >
                                    {BalanceLineTitle(row)}
                                </Mono>
                                <span className="flex shrink-0 items-center gap-1.5">
                                    <Mono className="text-mono-500 dark:text-mono-400">
                                        {FormatQty(row.qty_before)}
                                    </Mono>
                                    <ArrowRight size={12} className="text-mono-400" />
                                    <Mono
                                        className={
                                            changed ? 'font-bold text-black dark:text-white' : 'text-mono-500'
                                        }
                                    >
                                        {FormatQty(row.qty_after)}
                                    </Mono>
                                </span>
                            </li>
                        );
                    })}
                </ul>
            ) : null}

            {holder.emptied ? (
                <p className="flex items-start gap-1.5 rounded-lg bg-warning-soft px-2.5 py-2 text-xs font-medium text-warning-text dark:bg-warning-900/30 dark:text-warning-200">
                    <WarningCircle size={14} className="mt-0.5 shrink-0" />
                    <span>
                        <strong>{label('wms.repack.will_close')}.</strong> {label('wms.repack.will_close_hint')}
                    </span>
                </p>
            ) : null}
        </div>
    );
};

const BalancePreview = ({ balance, effects = null, label, compact = false }) => {
    if (!balance) {
        return null;
    }

    const holders = [...balance.holders].sort(
        (left, right) => DIRECTION_ORDER[HolderDirection(left)] - DIRECTION_ORDER[HolderDirection(right)],
    );

    return (
        <section className="space-y-3">
            {!compact ? (
                <header className="space-y-1">
                    <h3 className="text-base font-bold text-black dark:text-white">
                        {label('wms.repack.balance_title')}
                    </h3>
                    <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.repack.balance_hint')}</p>
                </header>
            ) : null}

            {/* 1. O VEREDITO — a pergunta real ("some alguma coisa?") respondida
                em uma linha, antes de qualquer detalhe. */}
            <p
                role="status"
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ${
                    balance.balanced
                        ? 'border-success-500 bg-success-soft text-success-text dark:border-success-700 dark:bg-success-900/30 dark:text-success-200'
                        : 'border-danger-500 bg-danger-soft text-danger-text dark:border-danger-700 dark:bg-danger-900/30 dark:text-danger-200'
                }`}
            >
                {balance.balanced ? (
                    <CheckCircle size={18} className="shrink-0" />
                ) : (
                    <WarningCircle size={18} className="shrink-0" />
                )}
                {balance.balanced ? label('wms.repack.balance_conserved') : label('wms.repack.balance_broken')}
            </p>

            {/* 2. Um cartão por LUGAR, na ordem do movimento. */}
            <div className={compact ? 'space-y-3' : 'grid gap-3 md:grid-cols-2'}>
                {holders.map((holder) => (
                    <HolderCard key={`${holder.kind}:${holder.key}`} holder={holder} label={label} />
                ))}
            </div>

            {/* 3. A PROVA por produto — pequena, mas sempre visível: é ela que
                sustenta o veredito da faixa verde. */}
            {balance.totals.length > 0 && !compact ? (
                <div className="rounded-lg border border-mono-200 bg-mono-50 p-3 dark:border-mono-800 dark:bg-mono-900/40">
                    <p className="mb-1.5 text-[11px] uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        {label('wms.repack.totals_title')}
                    </p>
                    <ul className="space-y-1">
                        {balance.totals.map((total) => (
                            <li
                                key={total.product_id}
                                className="flex items-center justify-between gap-3 text-xs text-mono-600 dark:text-mono-400"
                            >
                                <Mono className="min-w-0 flex-1 truncate">{total.sku || total.product_id}</Mono>
                                <span className="flex shrink-0 items-center gap-1.5">
                                    <Mono>{FormatQty(total.qty_before)}</Mono>
                                    <ArrowRight size={11} />
                                    <Mono>{FormatQty(total.qty_after)}</Mono>
                                    {total.balanced ? (
                                        <CheckCircle size={13} className="text-success-600 dark:text-success-400" />
                                    ) : (
                                        <Badge variant="danger">{FormatQty(total.qty_delta)}</Badge>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {/* 4. Consequências declaradas: o que NASCE e o que FECHA. */}
            {effects?.wouldCreate?.length ? (
                <p className="flex items-start gap-1.5 rounded-lg border border-dashed border-mono-300 px-3 py-2 text-xs text-mono-600 dark:border-mono-700 dark:text-mono-400">
                    <Plus size={14} className="mt-0.5 shrink-0" />
                    <span>
                        {`${label('wms.repack.will_create')}: `}
                        {effects.wouldCreate.map((draft, index) => (
                            <Mono key={`${draft.prefix_code}-${index}`} className="mr-2 font-semibold">
                                {draft.prefix_code}
                            </Mono>
                        ))}
                    </span>
                </p>
            ) : null}
        </section>
    );
};

export default BalancePreview;
